import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Card, CircularProgress } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { infoHubChartSchema } from "./infoHubChartSchema";
import { fetchInfoHubChartData } from "../../data/fetchInfoHubChartData";
import TableModesInfoHub from "./TableModesInfoHub";
import TableMultimodalConnections from "./TableMultimodalConnections";

// Register chart.js components
ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const COLOR_PALETTE = {
  car: "#eb5514",
  bus: "#cc0036",
  metro: "#E98500",
  tsimiski: "#0095b3",
};
const COLOR_POOL = [
  "#f4a261",
  "#2a9d8f",
  "#6a994e",
  "#8338ec",
  "#264653",
  "#ffb703",
  "#8ecae6",
];
const GLOBAL_COLOR_CACHE = {};
let colorIndex = 0;

function getColor(key) {
  const normalized = key.trim().toLowerCase();
  if (COLOR_PALETTE[normalized]) return COLOR_PALETTE[normalized];
  if (!GLOBAL_COLOR_CACHE[normalized]) {
    GLOBAL_COLOR_CACHE[normalized] = COLOR_POOL[colorIndex % COLOR_POOL.length];
    colorIndex++;
  }
  return GLOBAL_COLOR_CACHE[normalized];
}

// --- Data Transformer ---
function transformGeneric(raw, { xField, yField, groupField }) {
  const withDates = raw
    .map((d) => ({ ...d, __date: new Date(d[xField]) }))
    .sort((a, b) => a.__date - b.__date);

  const groups = Array.from(
    new Set(withDates.map((d) => d[groupField]?.toLowerCase()))
  );

  return {
    datasets: groups.map((group, index) => {
      const filtered = withDates.filter(
        (d) => d[groupField]?.toLowerCase() === group
      );

      const displayName = filtered[0]?.[groupField] ?? group;
      const color = getColor(group);

      return {
        label: displayName.charAt(0).toUpperCase() + displayName.slice(1),
        data: filtered
          .filter((d) => d[yField] !== null && d[yField] !== undefined)
          .map((d) => ({ x: d.__date, y: d[yField] })),
        borderColor: color,
        backgroundColor: color + "33",
        pointRadius: 2,
        tension: 0.2,
        fill: false,
        pathKey: group,
        isMain: true,
        index,
        spanGaps: false,
      };
    }),
  };
}

// --- Chart Options Generator ---
function generateChartOptions(cfg) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        time: {
          unit: "hour",
          displayFormats: { hour: "HH:mm" },
        },
        title: {
          display: true,
          text: cfg.axiosXLabel || i18n.t("trafficMonitoring.infohub.chart.axisTime"),
          color: "#fff",
          font: { size: 14 },
        },
        ticks: {
          color: "#fff",
          font: { size: 11 },
          autoSkip: true,
          maxRotation: 45,
          maxTicksLimit: 24,
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: cfg.axiosYLabel || i18n.t("trafficMonitoring.infohub.chart.axisValue"),
          color: "#fff",
          font: { size: 14 },
        },
        ticks: {
          color: "#fff",
          font: { size: 12 },
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: "#fff",
          generateLabels: (chart) => {
            const seen = {};
            return chart.data.datasets.reduce((items, d, i) => {
              const key = d.pathKey;
              if (!seen[key]) {
                seen[key] = true;
                const indices = chart.data.datasets
                  .map((ds, j) => (ds.pathKey === key ? j : -1))
                  .filter((j) => j >= 0);
                const mainLine = indices
                  .map((j) => chart.data.datasets[j])
                  .find((d) => d.isMain);
                items.push({
                  text: mainLine?.label ?? key,
                  fillStyle: mainLine?.borderColor,
                  hidden: !chart.isDatasetVisible(indices[0]),
                  datasetIndices: indices,
                });
              }
              return items;
            }, []);
          },
        },
        onClick: (e, legendItem, legend) => {
          const chart = legend.chart;
          legendItem.datasetIndices.forEach((i) => {
            const visible = chart.isDatasetVisible(i);
            chart.setDatasetVisibility(i, !visible);
          });
          chart.update();
        },
      },
    },
  };
}

// --- Main Component ---
function InfoHubChart() {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const fetchedCharts = await Promise.all(
          infoHubChartSchema.map(async (cfg) => {
            const raw = await fetchInfoHubChartData(cfg.endpoint);
            if (!Array.isArray(raw))
              throw new Error(`Invalid response from ${cfg.endpoint}`);
            return {
              id: cfg.id,
              name: cfg.name,
              data: transformGeneric(raw, cfg),
              options: generateChartOptions(cfg),
            };
          })
        );
        setChartData(fetchedCharts);
      } catch (err) {
        console.error("Error loading chart data:", err);
        setError(err.message || t("trafficMonitoring.infohub.chart.unknownError"));
      } finally {
        setLoading(false);
      }
    };

    load();

    const interval = setInterval(load, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
        >
          <CircularProgress size={50} color="primary" />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
        >
          <MDTypography variant="h6" color="error">
            {error}
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <>
      {chartData.map((chart) => (
        <Card key={chart.id} sx={{ mb: 2, height: 400 }}>
          <MDBox p={2}>
            <MDTypography variant="h6">{chart.name}</MDTypography>
          </MDBox>
          <MDBox sx={{ flexGrow: 1, p: 2 }}>
            <Line data={chart.data} options={chart.options} />
          </MDBox>
        </Card>
      ))}
      <MDBox display="flex" justifyContent="center" mb={2}>
        <MDButton
          variant="gradient"
          color="info"
          onClick={() =>
            window.open(
              "/thessaloniki/Thessaloniki_MCI_map.html",
              "_blank",
              "noopener,noreferrer"
            )
          }
        >
          {t("trafficMonitoring.infohub.chart.multimodalConnectivityIndex")}
        </MDButton>
      </MDBox>
      {/* <TableModesInfoHub />
      <TableMultimodalConnections /> */}
    </>
  );
}

export default InfoHubChart;
